import { normalizeGarageName, qrPath } from "@bt/domain";

/**
 * The garage is the URL's first segment: /my-garage/inventory (ADR 0006). The router runs below
 * it (basepath), so the app's own links stay garage-relative. Switching garage loads the page anew,
 * so nothing cached in one garage shows in another.
 */

const STORAGE_KEY = "bt-garage";

function firstSegment(pathname: string): string | null {
  const segment = pathname.split("/")[1];
  if (!segment) return null;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * The app's own pages at the start of the address (reserved as garage names, or too short for
 * one): /qr is where a scanned QR label lands (ADR 0018).
 */
export const SECTIONS = ["account", "admin", "qr"] as const;
export type Section = (typeof SECTIONS)[number];

/** /account/… (sign-in, invitations, profile), /admin/… or /qr/…; null in a garage or on "/". */
export const section: Section | null = (() => {
  const segment = firstSegment(window.location.pathname);
  return SECTIONS.find((s) => s === segment) ?? null;
})();

/** The garage in this page's URL, or null on "/" and the app's own pages. */
export const garage: string | null = (() => {
  const segment = firstSegment(window.location.pathname);
  if (segment === null || section) return null;
  return normalizeGarageName(segment) || null;
})();

/**
 * Brings the URL to the garage's normalized spelling (/myGarage1 → /mygarage1). Returns false
 * when the page is about to load at the new address.
 */
export function canonicalizeUrl(): boolean {
  // A label's address in any spelling; the server does the same where it is in front (Vite isn't).
  const qr = qrPath(window.location.pathname);
  if (qr.kind === "redirect" || qr.kind === "invalid") {
    window.location.replace(qr.kind === "redirect" ? `${qr.to}${window.location.search}` : qr.to);
    return false;
  }
  const segment = window.location.pathname.split("/")[1];
  if (garage === null || segment === garage) return true;
  const rest = window.location.pathname.split("/").slice(2).join("/");
  window.location.replace(`/${garage}/${rest}${window.location.search}${window.location.hash}`);
  return false;
}

/** /api/g/<garage><path>: the garage's API (tRPC, files, demo packages). */
export function apiPath(path: string, name = garage): string {
  return `/api/g/${name ?? ""}${path}`;
}

/** The address of a page in a garage. */
export function garageUrl(name: string, path = "/"): string {
  return `/${name}${path.startsWith("/") ? path : `/${path}`}`;
}

/** Opens a garage; a full page load, see above. */
export function openGarage(name: string, path = "/"): void {
  window.location.assign(garageUrl(name, path));
}

export function rememberGarage(name: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, name);
  } catch {
    // Not remembered; "/" then opens the first garage.
  }
}

export function lastGarage(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
